import baseApi from "./RootBaseApi";
import {
  TransactionItem,
  TransactionsListApiRaw,
  TransactionsListNormalized,
  TransactionsListParams,
  TransactionsSummary,
  TransactionsSummaryApiRaw,
  TransactionResponse,
} from "@/types/transactions.types";

export const TransactionsService = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getTransactionsList: builder.query<
      TransactionsListNormalized,
      TransactionsListParams
    >({
      query: (params) => ({
        url: "transactions/admin",
        method: "GET",
        params,
      }),
      transformResponse: (
        response: unknown,
        _meta,
        arg,
      ): TransactionsListNormalized => {
        const raw = response as TransactionsListApiRaw;
        const items: TransactionItem[] = (raw?.data || []).map((t) => ({
          id: String(t.id),
          type: String(t.type || ""),
          amount: Number(t.amount ?? 0),
          status: String(t.status || ""),
          payment_intent_id: t.payment_intent_id ?? null,
          paytrail_payment_id: t.paytrail_payment_id ?? null,
          stripe_refund_id: t.stripe_refund_id ?? null,
          refunded_amount: Number(t.refunded_amount ?? 0),
          currency: String(t.currency || "EUR"),
          created_at: String(t.created_at || ""),
          user_id: t.user_id ?? null,
          user: t.user ?? null,
        }));
        return {
          items,
          totalItems: Number(raw?.total_items ?? items.length),
          currentPage: Number(raw?.current_page ?? arg?.page ?? 1),
          totalPages: Number(raw?.total_pages ?? 1),
          pageLimit: Number(arg?.limit ?? items.length),
        };
      },
      providesTags: ["Transactions"],
      keepUnusedDataFor: 0,
    }),

    getTransactionsSummary: builder.query<TransactionsSummary, void>({
      query: () => ({
        url: "transactions/admin/stats/summary",
        method: "GET",
      }),
      transformResponse: (response: unknown): TransactionsSummary => {
        const raw = response as TransactionsSummaryApiRaw;
        const d = raw?.data || ({} as TransactionsSummary);
        return {
          totalTransactions: Number(d?.totalTransactions ?? 0),
          totalSuccessfulAmount: Number(d?.totalSuccessfulAmount ?? 0),
          failedTransactions: Number(d?.failedTransactions ?? 0),
          transactionsByType: Array.isArray(d?.transactionsByType)
            ? d.transactionsByType.map(
                (x: { type?: string; count?: number }) => ({
                  type: String(x.type || ""),
                  count: Number(x.count ?? 0),
                }),
              )
            : [],
        };
      },
      providesTags: ["Transactions"],
      keepUnusedDataFor: 0,
    }),

    getTransactionDetail: builder.query<TransactionResponse, string>({
      query: (id) => ({
        url: `transactions/admin/${id}`,
        method: "GET",
      }),
      transformResponse: (response: unknown): TransactionResponse => {
        return response as TransactionResponse;
      },
      providesTags: ["Transactions"],
      keepUnusedDataFor: 0,
    }),

    retryTransactionNetvisorInvoice: builder.mutation<void, string>({
      query: (id) => ({
        url: `transactions/admin/${id}/netvisor-invoice/retry`,
        method: "POST",
      }),
      invalidatesTags: ["Transactions"],
    }),

    exportTransactionsCsv: builder.query<
      Blob,
      Omit<TransactionsListParams, "page" | "limit">
    >({
      query: (params) => ({
        url: "transactions/admin/export",
        method: "GET",
        params,
        responseType: "blob",
      }),
      transformResponse: (response: unknown): Blob => response as Blob,
      keepUnusedDataFor: 0,
    }),
  }),
});

export const {
  useGetTransactionsListQuery,
  useGetTransactionsSummaryQuery,
  useGetTransactionDetailQuery,
  useExportTransactionsCsvQuery,
  useLazyExportTransactionsCsvQuery,
  useRetryTransactionNetvisorInvoiceMutation,
} = TransactionsService;
